import { HttpContext } from "@angular/common/http";
import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, ResolveFn } from "@angular/router";
import {
  catchError,
  combineLatest,
  map,
  Observable,
  of,
  switchMap,
} from "rxjs";
import { mergeDocumentLanguages } from "../helpers/document-languages.helper";
import { pathParamsHelper } from "../helpers/path-params.helper";
import { PatchedDefaultDocRef } from "../models/patched-default-doc-ref";
import { PiaDocumentsService } from "../pia-api/pia-documents.service";
import { PiaProductsService } from "../pia-api/pia-products.service";
import { DocumentLanguagesStorageService } from "../services/document-languages-storage.service";
import { IS_IN_RESOLVER_CONTEXT } from "../tokens/resolver-context.token";
import { withLoading } from "../utils/with-loading";

/**
 * used to get documents for a product series or range
 * @param route
 */
export const productDocumentsResolver: ResolveFn<Observable<unknown>> =
  withLoading((route: ActivatedRouteSnapshot): Observable<unknown> => {
    const piaDocumentsService = inject(PiaDocumentsService);
    const piaProductsService = inject(PiaProductsService);
    const documentLanguagesStorageService = inject(
      DocumentLanguagesStorageService,
    );

    const context = new HttpContext().set(IS_IN_RESOLVER_CONTEXT, true);

    const { productID, countryID, languageID, year, docCatID } =
      pathParamsHelper(route);

    if (!productID || !countryID || !languageID || isNaN(year) || !docCatID) {
      return of(null);
    }

    return combineLatest([
      documentLanguagesStorageService.primaryDocumentLanguages$,
      documentLanguagesStorageService.secondaryDocumentLanguages$,
      piaProductsService.getProductDisplayRangesProductIDs(
        { countryID, languageID, year, productID },
        context,
      ),
    ]).pipe(
      map(
        ([primaryDocumentLanguages, secondaryDocumentLanguages, productIDs]: [
          Record<string, string[]> | null,
          Record<string, string[]> | null,
          string[],
        ]): [string[], string[]] => {
          const documentLanguages = mergeDocumentLanguages(
            primaryDocumentLanguages,
            secondaryDocumentLanguages,
            countryID,
            languageID,
          );

          return [documentLanguages, productIDs];
        },
      ),
      switchMap(([includedLanguages, productIDs]) => {
        return piaDocumentsService.getDocumentsByCountryLanguageYearProductAndCategory(
          {
            countryID,
            year,
            productIDs,
            docCatID,
            includedLanguages: includedLanguages.length
              ? includedLanguages
              : [languageID.toLowerCase()],
          },
          context,
        );
      }),
      map((results: PatchedDefaultDocRef[]) => {
        // console.log(
        //   'results',
        //   JSON.stringify(
        //     results,
        //     (key, value) => {
        //       if (['__metadata'].includes(key)) return undefined;
        //       return value;
        //     },
        //     4
        //   )
        // );
        return results;
      }),
      catchError(() => of(null)),
    );
  });
